'use strict';

var utils = require('@react-aria/utils');
const visualViewport = typeof window !== 'undefined' && window.visualViewport;
const nonTextInputTypes = new Set(['checkbox', 'radio', 'range', 'color', 'file', 'image', 'button', 'submit', 'reset']);
const isVerticalScrollable = node => {
  const style = window.getComputedStyle(node);
  return /(auto|scroll)/.test(style.overflowY);
};
const isHorizontalScrollable = node => {
  const style = window.getComputedStyle(node);
  return /(auto|scroll)/.test(style.overflowX);
};
const setStyle = (element, style, value) => {
  const styleAsIndex = style;
  const cur = element.style[styleAsIndex];
  element.style[styleAsIndex] = value;
  return () => {
    element.style[styleAsIndex] = cur;
  };
};
const scrollIntoView = target => {
  const scrollable = utils.getScrollParent(target);
  if (scrollable !== document.documentElement && scrollable !== document.body) {
    const scrollableTop = scrollable.getBoundingClientRect().top;
    const targetTop = target.getBoundingClientRect().top;
    if (targetTop > scrollableTop + target.clientHeight) {
      scrollable.scrollTop += targetTop - scrollableTop;
    }
  }
};
const addEvent = (target, event, handler, options) => {
  target.addEventListener(event, handler, options);
  return () => {
    target.removeEventListener(event, handler, options);
  };
};
const preventScrollStandard = () => utils.chain(setStyle(document.documentElement, 'paddingRight', "".concat(window.innerWidth - document.documentElement.clientWidth, "px")), setStyle(document.documentElement, 'overflow', 'hidden'));
const preventScrollMobileSafari = () => {
  let scrollable;
  let lastY = 0;
  let lastX = 0;
  const onTouchStart = e => {
    scrollable = utils.getScrollParent(e.target);
    if (scrollable === document.documentElement && scrollable === document.body) {
      return;
    }
    lastY = e.changedTouches[0].pageY;
    lastX = e.changedTouches[0].pageX;
  };
  const onTouchMove = e => {
    if (scrollable === document.documentElement || scrollable === document.body) {
      e.preventDefault();
      return;
    }
    const y = e.changedTouches[0].pageY;
    const x = e.changedTouches[0].pageX;
    const {
      scrollTop,
      scrollLeft
    } = scrollable;
    const bottom = scrollable.scrollHeight - scrollable.clientHeight;
    const right = scrollable.scrollWidth - scrollable.clientWidth;
    const scrollingUpAtTop = scrollTop <= 0 && y > lastY;
    const scrollingDownAtBottom = scrollTop >= bottom && y < lastY;
    const scrollingLeftAtBeginning = scrollLeft <= 0 && x > lastX;
    const scrollingRightAtEnd = scrollLeft >= right && x < lastX;
    if (isVerticalScrollable(scrollable) && !isHorizontalScrollable(scrollable)) {
      if (scrollingUpAtTop || scrollingDownAtBottom) {
        e.preventDefault();
      }
    } else if (isHorizontalScrollable(scrollable) && !isVerticalScrollable(scrollable)) {
      if (scrollingLeftAtBeginning || scrollingRightAtEnd) {
        e.preventDefault();
      }
    }
    lastY = y;
    lastX = x;
  };
  const onTouchEnd = e => {
    const {
      target
    } = e;
    if (target instanceof HTMLInputElement && !nonTextInputTypes.has(target.type) && target !== document.activeElement) {
      e.preventDefault();
      target.focus();
      requestAnimationFrame(() => {
        target.style.transform = '';
      });
    }
  };
  const onFocus = e => {
    const {
      target
    } = e;
    if (target instanceof HTMLInputElement && !nonTextInputTypes.has(target.type)) {
      requestAnimationFrame(() => {
        target.style.transform = '';
        if (visualViewport) {
          if (visualViewport.height < window.innerHeight) {
            requestAnimationFrame(() => {
              scrollIntoView(target);
            });
          } else {
            visualViewport.addEventListener('resize', () => scrollIntoView(target), {
              once: true
            });
          }
        }
      });
    }
  };
  const onWindowScroll = e => {
    const {
      scrollingElement
    } = e.target;
    if (!(scrollingElement !== null && scrollingElement !== void 0 && scrollingElement.customScrolling)) {
      e.preventDefault();
    } else {
      document.documentElement.style.overflow = 'auto';
      setTimeout(() => {
        document.documentElement.style.overflow = 'hidden';
      }, 500);
    }
  };
  const scrollX = window.pageXOffset;
  const scrollY = window.pageYOffset;
  const restoreStyles = utils.chain(setStyle(document.documentElement, 'paddingRight', "".concat(window.innerWidth - document.documentElement.clientWidth, "px")), setStyle(document.documentElement, 'overflow', 'hidden'));
  const removeEvents = utils.chain(addEvent(document, 'touchstart', onTouchStart, {
    passive: false,
    capture: true
  }), addEvent(document, 'touchmove', onTouchMove, {
    passive: false,
    capture: true
  }), addEvent(document, 'touchend', onTouchEnd, {
    passive: false,
    capture: true
  }), addEvent(document, 'focus', onFocus, true), addEvent(window, 'scroll', onWindowScroll));
  return () => {
    restoreStyles();
    removeEvents();
    window.scrollTo(scrollX, scrollY);
  };
};
let preventScrollCount = 0;
let restore;
const usePreventScroll = function () {
  let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    isDisabled
  } = options;
  utils.useLayoutEffect(() => {
    if (isDisabled) {
      return;
    }
    preventScrollCount += 1;
    if (preventScrollCount === 1) {
      if (utils.isIOS()) {
        restore = preventScrollMobileSafari();
      } else {
        restore = preventScrollStandard();
      }
    }
    return () => {
      preventScrollCount -= 1;
      if (preventScrollCount === 0) {
        restore();
      }
    };
  }, [isDisabled]);
};
module.exports = usePreventScroll;
